import { lazy, Suspense } from "react";
import { Switch, Route, Redirect } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";

// Pages — lazy-loaded so each route ships its own chunk instead of one 2 MB bundle.
const LoginPage = lazy(() => import("@/pages/login-page"));
const RegisterPage = lazy(() => import("@/pages/register-page"));
const ReviewsPage = lazy(() => import("@/pages/reviews-page"));
const BusinessProfilePage = lazy(() => import("@/pages/business-profile-page"));
const ReviewGenerationPage = lazy(() => import("@/pages/review-generation-page"));
const StatusPage = lazy(() => import("@/pages/status-page"));
const SettingsPage = lazy(() => import("@/pages/settings-page"));
const SetupPage = lazy(() => import("@/pages/setup-page"));
const ClaimAccountPage = lazy(() => import("@/pages/claim-account-page"));
const SmsPage = lazy(() => import("@/pages/sms-page"));
const ReviewAnalyticsPage = lazy(() => import("@/pages/review-analytics-page"));
const ActivityAnalyticsPage = lazy(() =>
  import("@/pages/review-analytics-page").then((m) => ({ default: m.ActivityAnalyticsPage }))
);
const ReviewExchangeLandingPage = lazy(() => import("@/pages/review-exchange-page"));
const ReviewExchangeAppPage = lazy(() =>
  import("@/pages/review-exchange-page").then((m) => ({ default: m.ReviewExchangeAppPage }))
);
const ExchangeDetailPage = lazy(() => import("@/pages/exchange-detail-page"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password-page"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password-page"));
const AdminExchangePriorityPage = lazy(() => import("@/pages/admin-exchange-priority-page"));
const AdminPage = lazy(() => import("@/pages/admin-page"));
const RankingPage = lazy(() => import("@/pages/ranking-page"));
const OtherRankingPage = lazy(() =>
  import("@/pages/ranking-page").then((m) => ({ default: m.OtherRankingPage })),
);
const RankingNewPage = lazy(() => import("@/pages/ranking-new-page"));
const RankingPrintPage = lazy(() => import("@/pages/ranking-print-page"));
const RankingCreditsPage = lazy(() => import("@/pages/ranking-credits-page"));
const ViewPatternsPage = lazy(() => import("@/pages/view-patterns-page"));

// Marketing Pages
const LandingPage = lazy(() => import("@/pages/landing-page"));
const FeaturesPage = lazy(() => import("@/pages/features-page"));
const PricingPage = lazy(() => import("@/pages/pricing-page"));
const TermsPage = lazy(() => import("@/pages/terms-page"));

// Loading spinner for auth check
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-m-bg-primary flex items-center justify-center px-4">
      <div className="air-panel flex min-w-44 flex-col items-center gap-3 px-6 py-5">
        <Loader2 className="h-5 w-5 animate-spin text-m-accent-purple" strokeWidth={1.75} />
        <p className="text-sm text-m-text-secondary">Loading AIReview…</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  return <>{children}</>;
}

// Owner/Admin route wrapper (for settings)
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, canAccessSettings } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (!canAccessSettings()) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

// True-admin route wrapper — gates pages only role='admin' users can access.
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (!isAdmin()) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

// Non-technician route wrapper — gates pages technicians shouldn't access
// (outreach). Other authenticated users pass through.
function NonTechnicianRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isTechnician } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (isTechnician()) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children, redirectTo = "/" }: { children: React.ReactNode; redirectTo?: string }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Redirect to={redirectTo} />;
  }

  return <>{children}</>;
}

// Landing or Dashboard based on auth state
function LandingOrDashboard() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return user ? <ReviewsPage /> : <LandingPage />;
}

// Outreach: app page for authenticated (non-technician), landing page for guests.
// Technicians are redirected home — they don't have access to outreach.
function OutreachRouter() {
  const { user, isLoading, isTechnician } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (user && isTechnician()) {
    return <Redirect to="/" />;
  }

  return user ? <ReviewExchangeAppPage /> : <ReviewExchangeLandingPage />;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {/* Marketing pages (public) */}
      <Route path="/features">
        <FeaturesPage />
      </Route>

      <Route path="/pricing">
        <PricingPage />
      </Route>

      <Route path="/terms">
        <TermsPage />
      </Route>

      <Route path="/outreach/:id">
        <NonTechnicianRoute>
          <ExchangeDetailPage />
        </NonTechnicianRoute>
      </Route>

      <Route path="/outreach">
        <OutreachRouter />
      </Route>

      {/* Legacy: redirect old review-exchange URLs to /outreach */}
      <Route path="/review-exchange/:id">{(params) => <Redirect to={`/outreach/${params.id}`} />}</Route>
      <Route path="/review-exchange">
        <Redirect to="/outreach" />
      </Route>

      {/* Auth routes */}
      <Route path="/login">
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      </Route>

      <Route path="/register">
        <PublicRoute redirectTo="/setup">
          <RegisterPage />
        </PublicRoute>
      </Route>

      {/* Password reset */}
      <Route path="/forgot-password">
        <ForgotPasswordPage />
      </Route>

      <Route path="/reset-password/:token">
        <ResetPasswordPage />
      </Route>

      {/* Claim sponsored account */}
      <Route path="/claim/:slug">
        <ClaimAccountPage />
      </Route>

      {/* Post-registration setup: link Google Maps profile */}
      <Route path="/setup">
        <ProtectedRoute>
          <SetupPage />
        </ProtectedRoute>
      </Route>

      {/* Public status page (customer-facing) - review-for-benefit exchange */}
      <Route path="/here/:slug/:shortCode/status">
        <StatusPage />
      </Route>

      <Route path="/here/:slug/status">
        <StatusPage />
      </Route>

      {/* Public review generation page (customer-facing) - ai-review.us/here/{slug}.
          Keep these generic routes after the more-specific /status patterns. */}
      <Route path="/here/:slug/:shortCode">
        <ReviewGenerationPage />
      </Route>

      <Route path="/here/:slug">
        <ReviewGenerationPage />
      </Route>

      {/* Legacy /r/ routes - redirect to /here/ */}
      <Route path="/r/:slug/status">{(params) => <Redirect to={`/here/${params.slug}/status`} />}</Route>
      <Route path="/r/:slug/:shortCode/status">{(params) => <Redirect to={`/here/${params.slug}/${params.shortCode}/status`} />}</Route>
      <Route path="/r/:slug/:shortCode">{(params) => <Redirect to={`/here/${params.slug}/${params.shortCode}`} />}</Route>
      <Route path="/r/:slug">{(params) => <Redirect to={`/here/${params.slug}`} />}</Route>

      {/* Legacy URLs created by older dashboard/SMS builds. Keep them working. */}
      <Route path="/review/:slug/:shortCode">{(params) => <Redirect to={`/here/${params.slug}/${params.shortCode}`} />}</Route>
      <Route path="/review/:slug">{(params) => <Redirect to={`/here/${params.slug}`} />}</Route>

      {/* Protected routes */}
      {/* Legacy routes - redirect to "/" */}
      <Route path="/dashboard">
        <Redirect to="/" />
      </Route>

      <Route path="/reviews">
        <Redirect to="/" />
      </Route>

      <Route path="/business-profile">
        <ProtectedRoute>
          <BusinessProfilePage />
        </ProtectedRoute>
      </Route>

      {/* Legacy redirects */}
      <Route path="/company-reviews">
        <Redirect to="/business-profile" />
      </Route>

      <Route path="/google-profile">
        <Redirect to="/business-profile" />
      </Route>

      <Route path="/settings">
        <OwnerRoute>
          <SettingsPage />
        </OwnerRoute>
      </Route>

      <Route path="/config-settings">
        <OwnerRoute>
          <SettingsPage />
        </OwnerRoute>
      </Route>

      <Route path="/sms">
        <ProtectedRoute>
          <SmsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/ranking/new">
        <ProtectedRoute>
          <RankingNewPage />
        </ProtectedRoute>
      </Route>

      <Route path="/ranking/credits">
        <ProtectedRoute>
          <RankingCreditsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/ranking/:gridId/print">
        <ProtectedRoute>
          <RankingPrintPage />
        </ProtectedRoute>
      </Route>

      {/* Opaque share-token URLs — public, no ProtectedRoute. Mirrors
          scraper-crm's /rank-map/<token>[/<position>] alias for rank
          reports so the legacy /r/<slug> → /here/<slug> redirects
          below (review-collection back-compat) don't intercept them. */}
      <Route path="/rank-map/:token/:position">
        <RankingPrintPage />
      </Route>
      <Route path="/rank-map/:token">
        <RankingPrintPage />
      </Route>

      <Route path="/ranking/other/:scraperGridId">
        {(params) => (
          <ProtectedRoute>
            <OtherRankingPage scraperGridId={parseInt(params.scraperGridId, 10)} />
          </ProtectedRoute>
        )}
      </Route>

      <Route path="/ranking/:gridId">
        {(params) => (
          <ProtectedRoute>
            <RankingPage gridId={params.gridId} />
          </ProtectedRoute>
        )}
      </Route>

      <Route path="/ranking">
        <ProtectedRoute>
          <RankingPage />
        </ProtectedRoute>
      </Route>

      {/* Sponsorship UI is hidden — redirect any deep links home */}
      <Route path="/sponsorship">
        <Redirect to="/" />
      </Route>

      <Route path="/thank-sponsor">
        <Redirect to="/" />
      </Route>

      <Route path="/admin/exchange-priority">
        <OwnerRoute>
          <AdminExchangePriorityPage />
        </OwnerRoute>
      </Route>

      <Route path="/admin">
        <AdminRoute>
          <AdminPage />
        </AdminRoute>
      </Route>

      {/* Living reference for the shared view patterns (docs/view-patterns-port.md).
          Open in dev builds; admin-only in production. */}
      <Route path="/dev/view-patterns">
        {import.meta.env.DEV ? (
          <ViewPatternsPage />
        ) : (
          <AdminRoute>
            <ViewPatternsPage />
          </AdminRoute>
        )}
      </Route>

      <Route path="/activity/:slug/:shortCode">
        <ProtectedRoute>
          <ActivityAnalyticsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/review-analytics">
        <ProtectedRoute>
          <ReviewAnalyticsPage />
        </ProtectedRoute>
      </Route>

      <Route path="/review-requests/:id/analytics">
        <ProtectedRoute>
          <ReviewAnalyticsPage />
        </ProtectedRoute>
      </Route>

      {/* Root: Landing page for guests, Dashboard for authenticated users */}
      <Route path="/">
        <LandingOrDashboard />
      </Route>

      {/* Fallback */}
      <Route>
        <Redirect to="/" />
      </Route>
    </Switch>
    </Suspense>
  );
}
